import cx from 'clsx';
import { CSSProperties, useEffect, useRef } from 'react';

import './MathLiveField.css';
import { adoptAnswerSlotStyles } from './answerSlotStyles';
import {
  applySlotStates,
  attachSlotLabeling,
  clearFillAtCaret,
  deleteKeepingSlotSize,
  fitEmptySlots,
  hasAllSlots,
  moveCaretIntoSlot,
  moveCaretToAdjacentSlot,
  numberSlots,
  readSlots,
  sameSlotValues,
  seedTemplate,
  setTemplateKeepingCaret,
  tagSlots,
  writeSlots,
  type SlotStates,
  type SlotValues
} from './answerSlots';
import { attachDigitGrouping } from './groupDigits';
import { MATH_ANSWER_SLOT_LAYOUT, MATH_REQUIRED_SYMBOLS_LAYOUT } from './mathKeyboardLayouts';
import { ensureVirtualKeyboardInsetMirror } from './virtualKeyboardInsetMirror';
import { startVirtualKeyboardLift } from './virtualKeyboardLift';
import type { MathKeyboardId } from '../../utils/evaluation/math/mathKeyboardIds';
import { normalizeMathLatex } from '../../utils/evaluation/math/normalizeMathLatex';
import { useMathKeyboard } from '../MathKeyboard/MathKeyboardContext';

// Minimal shape of the mathlive <math-field> custom element we rely on.
export type MathFieldElement = HTMLElement & {
  disabled?: boolean;
  readOnly?: boolean;
  position?: number;
  isSelectionEditable?: boolean;
  getPrompts?: () => string[];
  getPromptValue?: (id: string, format?: string) => string;
  setPromptValue?: (id: string, content: string, options?: Record<string, unknown>) => void;
  getPromptRange?: (id: string) => [number, number] | null;
  setPromptState?: (id: string, state: 'correct' | 'incorrect' | 'undefined', locked?: boolean) => void;
  getElementInfo?: (offset: number) => { bounds?: DOMRect } | undefined;
  mathVirtualKeyboardPolicy?: 'auto' | 'manual' | 'sandboxed';
  menuItems?: unknown[];
  getValue?: (format?: string) => string;
  setValue?: (value: string, options?: Record<string, unknown>) => void;
  insert?: (latex: string, options?: { selectionMode?: 'placeholder' | 'after' | 'before' | 'all' }) => boolean;
  executeCommand?: (selector: string) => boolean;
  onExport?: (from: MathFieldElement, latex: string, range: unknown) => string;
};

// Author keyboard: the full stock keyboard (`'default'`) PLUS the required-symbols layer — and, in an
// slot-expression editor, the student-slot layer first, so the key that marks a slot is the first thing
// the author sees. Students do NOT use MathLive's virtual keyboard at all — a custom on-screen keyboard
// (MathKeyboardProvider) is shown instead (see `studentKeyboard`). The keyboard is a shared singleton,
// re-applied on focus.
function applyKeyboardLayout(virtualKeyboard: boolean, editsSlotExpression?: boolean) {
  if (!virtualKeyboard || !window.mathVirtualKeyboard) return;
  window.mathVirtualKeyboard.layouts = editsSlotExpression
    ? [MATH_ANSWER_SLOT_LAYOUT, 'default', MATH_REQUIRED_SYMBOLS_LAYOUT]
    : ['default', MATH_REQUIRED_SYMBOLS_LAYOUT];
}

// Minimal shape of a mathlive contextual-menu item descriptor (we only touch the "copy" entry).
type MathMenuItem = { id?: string; label?: unknown };

/**
 * Export BARE LaTeX to the `text/plain` clipboard entry instead of mathlive's default `$$ … $$`
 * wrapper. This fixes the on-screen keyboard's PASTE key (bug 529112).
 *
 * The paste key runs mathlive's `pasteFromClipboard` command, which reads the clipboard via
 * `navigator.clipboard.readText()` — that returns ONLY `text/plain` (the richer `application/x-latex`
 * and `application/json+mathlive` entries a real paste event carries are invisible to it). It then
 * feeds that string straight into `mathfield.insert()`, WITHOUT the `trimModeShiftCommand` step the
 * real paste-event path uses. So mathlive's default `$$ <latex> $$` text/plain form is re-parsed as
 * math with the leading `$$ ` mode-shift intact, mangling the formula (e.g. the first `\frac` loses
 * its backslash). Emitting the un-delimited LaTeX means the paste key inserts exactly what was
 * copied. The copy event still sets `application/x-latex` alongside, so manual Ctrl+V into rich text
 * (handled by MathLivePaste / `extractPastedLatex`) is unaffected.
 */
function exportBareLatex(_from: MathFieldElement, latex: string) {
  return latex;
}

/**
 * Re-point the contextual menu's "Copy" at mathlive's `copyToClipboard` COMMAND.
 *
 * mathlive's default menu "Copy" (a submenu of "Copy as LaTeX / Typst / ASCIIMath / MathML") calls
 * `ModeEditor.copyToClipboard`, which does `navigator.clipboard.writeText(latex)` — that writes a
 * `text/plain` entry ONLY. The tiptap paste handler (MathLivePaste / `extractPastedLatex`) recognises
 * a formula via the `application/x-latex` entry or a `$$ … $$`-delimited payload, and `writeText`
 * sets neither, so a formula copied from the menu pastes as raw LaTeX text instead of a rendered
 * formula (bug 529112, second case). The `copyToClipboard` command instead goes through the native
 * copy-event path (`onCopy`), which sets `application/x-latex` — the same rich clipboard Ctrl+C and
 * the on-screen [Copy] key already produce. Flattening the submenu to a single command-backed "Copy"
 * makes every author copy path consistent and pasteable.
 */
function normalizeCopyMenu(element: MathFieldElement) {
  // Reading/writing `menuItems` reaches into mathlive's menu internals; never let a shape change
  // there break the field itself — the copy tweak is a nicety, seeding/focus is not.
  try {
    const items = element.menuItems;
    if (!Array.isArray(items) || items.length === 0) return;
    element.menuItems = items.map(item => {
      const menuItem = item as MathMenuItem;
      if (menuItem?.id !== 'copy') return item;
      return {
        id: 'copy',
        label: menuItem.label,
        onMenuSelect: () => element.executeCommand?.('copyToClipboard')
      };
    });
  } catch {
    // Leave the default menu in place if mathlive's menu API shifts.
  }
}

export type MathLiveFieldProps = {
  value?: string;
  onChange?: (value: string) => void;
  // A student field inside a slot expression (PBI 538086): the field shows this expression read-only
  // and only its slots are editable. The slots' contents travel as `slotValues` / `onSlotsChange`, keyed
  // by slot id; `value` and `onChange` are unused.
  slotExpression?: string;
  slotValues?: SlotValues;
  onSlotsChange?: (values: SlotValues) => void;
  // Each slot's grading result, drawn on the slot itself.
  slotStates?: SlotStates;
  // Names each student slot for a page-scanning agent: `data-agent-step-id` on the slot's frame, holding
  // what this answers for the slot's id (see `tagSlots`). Slot fields only; an author editor leaves it unset.
  agentStepIdForSlot?: (slotId: string) => string | undefined;
  // The author's slot-expression editor: the student-slot key is on its keyboard, normalizing keeps the
  // (empty) slots, and re-seeding it keeps the caret beside the slot the author was working on.
  editsSlotExpression?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  // Render the on-screen keyboard inside the current frame. Needed when the field
  // lives in an iframe (e.g. the player), otherwise mathlive delegates the keyboard
  // to the top window and it never appears.
  sandboxed?: boolean;
  // Hide mathlive's contextual menu (the "hamburger" toggle) — e.g. for students.
  hideMenu?: boolean;
  // Graded answer fields only (a Cloze Math correct answer or exercise, the student's blank): run the
  // LaTeX this field emits through `normalizeMathLatex`, so invisible material — empty script
  // branches, spacing commands, format characters — never reaches storage or grading (PBI 535629).
  // Left off for display-only editors, where spacing commands are legitimate layout.
  normalizeLatex?: boolean;
  // When set, this is a STUDENT field: MathLive's virtual keyboard is disabled and the custom
  // on-screen keyboard (this layout) is shown via MathKeyboardProvider. Author fields leave it unset.
  studentKeyboard?: MathKeyboardId;
  // Allow MathLive's on-screen virtual keyboard (authors). When false, the field is typeset-only via
  // the physical keyboard and the math keyboard never opens.
  virtualKeyboard?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * Thin React wrapper around mathlive's `<math-field>` web component. Reads and
 * writes LaTeX, lazy-loading the `mathlive` package (which registers the custom
 * element) on first mount. Authors get MathLive's on-screen keyboard; students get
 * the custom `MathKeyboard` panel instead (driven via `MathKeyboardProvider`).
 */
export function MathLiveField({
  slotExpression,
  slotStates,
  slotValues,
  agentStepIdForSlot,
  editsSlotExpression,
  onSlotsChange,
  value = '',
  onChange,
  autoFocus,
  disabled,
  sandboxed,
  hideMenu,
  normalizeLatex,
  studentKeyboard,
  virtualKeyboard = true,
  className,
  style
}: MathLiveFieldProps) {
  const ref = useRef<MathFieldElement | null>(null);
  const mathKeyboard = useMathKeyboard();
  // Latest value, read inside the async loader so it seeds the field post-upgrade.
  const valueRef = useRef(value);
  valueRef.current = value;
  const slotExpressionRef = useRef(slotExpression);
  slotExpressionRef.current = slotExpression;
  const slotValuesRef = useRef(slotValues);
  slotValuesRef.current = slotValues;
  const slotStatesRef = useRef(slotStates);
  slotStatesRef.current = slotStates;
  // Read at label time, so a caller passing a fresh closure each render neither re-arms the observer nor
  // tags with a stale one.
  const agentStepIdForSlotRef = useRef(agentStepIdForSlot);
  agentStepIdForSlotRef.current = agentStepIdForSlot;
  const tagsSlots = agentStepIdForSlot !== undefined;
  // The expression the element currently shows, so an expression edited in Studio re-seeds the preview.
  const seededTemplateRef = useRef<string | undefined>(undefined);

  // Load mathlive, then seed the field with the current value and focus — but only once
  // the <math-field> element has upgraded. Setting the value before upgrade is dropped,
  // which is why a freshly opened editor would otherwise appear empty.
  useEffect(() => {
    let cancelled = false;
    let detachDigitGrouping: (() => void) | undefined;
    let detachSlotLabeling: (() => void) | undefined;

    const init = async () => {
      if (!window.customElements.get('math-field')) {
        await import('mathlive');
      }
      await window.customElements.whenDefined('math-field');
      if (cancelled) return;

      const element = ref.current;
      if (!element?.setValue) return;

      // Students get the custom keyboard, so MathLive's is kept fully manual (never auto-opens);
      // authors get 'sandboxed' (in-frame) or the default policy.
      if (!virtualKeyboard || studentKeyboard) element.mathVirtualKeyboardPolicy = 'manual';
      else if (sandboxed) element.mathVirtualKeyboardPolicy = 'sandboxed';
      // Export bare LaTeX to text/plain so the on-screen keyboard's Paste key round-trips (529112).
      element.onExport = exportBareLatex;
      // Empty menu items make mathlive hide its contextual-menu ("hamburger") toggle. Authors keep
      // the menu, but its "Copy" is re-pointed so the copied formula pastes into rich text (529112).
      if (hideMenu) element.menuItems = [];
      else normalizeCopyMenu(element);
      // The keyboard is one shared singleton and every field re-applies its layout on focus, so only a field
      // that already has focus may set it here — a field mounting beside the focused one (a new answer row
      // under the slot expression) would otherwise replace that field's layout under the author's hands.
      if (!studentKeyboard && (autoFocus || document.activeElement === element)) {
        applyKeyboardLayout(virtualKeyboard, editsSlotExpression);
      }
      // Containers (modals) track the keyboard's height via a global CSS var — see the mirror.
      if (virtualKeyboard && !studentKeyboard) ensureVirtualKeyboardInsetMirror();
      const template = slotExpressionRef.current;
      if (template !== undefined || editsSlotExpression) adoptAnswerSlotStyles(element);
      if (template !== undefined) {
        seedTemplate(element, template, slotValuesRef.current);
        applySlotStates(element, slotStatesRef.current);
      } else {
        element.setValue(valueRef.current ?? '');
      }
      seededTemplateRef.current = template;
      // Every math EDITOR paints thousands separators (80000 reads as 80,000 — bug 535512), like the
      // legacy LO equation editor. Display-only: the comma is a CSS ::after on the rendered digit
      // spans, the LaTeX value never contains it — so static renders (KaTeX in rich text) stay
      // ungrouped, which also matches LO.
      detachDigitGrouping = attachDigitGrouping(element);
      // An author's editor numbers its slots; a student's slot field names them for the agent. One observer
      // on the shadow root serves whichever labels this field takes.
      const labels = [
        ...(editsSlotExpression ? [numberSlots] : []),
        ...(template !== undefined && tagsSlots
          ? [
              (field: MathFieldElement) => {
                const idFor = agentStepIdForSlotRef.current;
                if (idFor) tagSlots(field, idFor);
              }
            ]
          : [])
      ];
      if (labels.length > 0) {
        detachSlotLabeling = attachSlotLabeling(element, field => labels.forEach(label => label(field)));
      }
      if (autoFocus) {
        element.focus();
        if (studentKeyboard) mathKeyboard.showKeyboard(element, studentKeyboard);
        else if (virtualKeyboard) window.mathVirtualKeyboard?.show();
      }
    };

    void init();

    return () => {
      cancelled = true;
      detachDigitGrouping?.();
      detachSlotLabeling?.();
    };
  }, [autoFocus, sandboxed, hideMenu, virtualKeyboard, studentKeyboard, mathKeyboard, editsSlotExpression, tagsSlots]);

  // Reflect later external value changes and emit the user's edits as LaTeX.
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // What the field emits for storage. Normalizing fields compare through the same function: while
    // an author has an empty subscript open (`x_{\placeholder{}}`) the stored value is `x`, and a raw
    // comparison would write `x` back into the field and delete the branch under the caret.
    const toStored = (latex: string) =>
      normalizeLatex ? normalizeMathLatex(latex, { keepPrompts: editsSlotExpression }) : latex;
    const inTemplate = slotExpression !== undefined;

    if (element.setValue && element.getValue) {
      if (inTemplate) {
        if (seededTemplateRef.current !== slotExpression || !hasAllSlots(element, slotExpression)) {
          adoptAnswerSlotStyles(element);
          seedTemplate(element, slotExpression, slotValues);
          seededTemplateRef.current = slotExpression;
        } else {
          writeSlots(element, slotValues, toStored);
        }
      } else if (seededTemplateRef.current !== undefined) {
        // The expression was taken away: back to a plain, fully editable field.
        element.readOnly = false;
        element.setValue(value);
        seededTemplateRef.current = undefined;
      } else if (toStored(element.getValue('latex')) !== toStored(value)) {
        if (editsSlotExpression && document.activeElement === element) setTemplateKeepingCaret(element, value);
        else element.setValue(value);
      }
    }

    const emitChange = () => {
      if (!inTemplate) {
        onChange?.(toStored(element.getValue ? element.getValue('latex') : ''));
        return;
      }
      fitEmptySlots(element, toStored);
      const next = readSlots(element, toStored);
      if (!sameSlotValues(next, slotValues)) onSlotsChange?.(next);
    };
    const handleInput = () => emitChange();
    // mathlive 0.106 dispatches no `input` for a physical keystroke into a read-only field's prompt —
    // only `beforeinput`, ahead of the edit. Read the slots once the edit has landed. An empty slot's
    // invisible digit must change in the same task as the edit, or the slot jumps for a frame: it
    // leaves ahead of an insert (`clearFillAtCaret`), and a delete is performed here so the digit is
    // back before the browser paints (`deleteKeepingSlotSize`).
    const handleBeforeInput = (event: Event) => {
      const input = event as InputEvent;
      if (inTemplate && deleteKeepingSlotSize(element, input, toStored)) {
        emitChange();
        return;
      }
      if (inTemplate && input.inputType?.startsWith('insert')) clearFillAtCaret(element);
      window.setTimeout(emitChange, 0);
    };
    // Keep the caret in a slot: after a click (mathlive may place it from the click only after
    // `pointerup`), and again ahead of a keystroke, in the capture phase, before mathlive handles it.
    const handleCaret = () => moveCaretIntoSlot(element);
    // Tab moves between the slots in reading order; past the last (or first) slot it leaves the field.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && moveCaretToAdjacentSlot(element, event.shiftKey ? -1 : 1)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      handleCaret();
    };
    const handlePointerUp = () => window.setTimeout(handleCaret, 0);
    const handleFocus = () => {
      if (inTemplate) handleCaret();
      // Student: open the custom keyboard for this field. Author: re-apply the shared MathLive
      // layout (singleton) — but do NOT force-show, so the keyboard toggle can still hide it.
      if (studentKeyboard) {
        mathKeyboard.showKeyboard(element, studentKeyboard);
        return;
      }
      applyKeyboardLayout(virtualKeyboard, editsSlotExpression);
    };
    const handlePointerDown = (event: Event) => {
      // Clicks on mathlive's own toggle buttons must NOT force-show — let them toggle.
      // The <math-field> uses an OPEN shadow root, so `event.target` is RETARGETED to the host and
      // `target.closest('[part=…]')` can never see the shadow-DOM toggles. Walk `composedPath()`,
      // which pierces the shadow boundary, instead. Without this the pointerdown force-shows the
      // keyboard a beat before mathlive's toggle handler runs, which then sees it visible and hides
      // it — the open-then-immediately-close flicker (bug 530337).
      const onToggle = event.composedPath().some(node => {
        const part = node instanceof Element ? node.getAttribute('part') : null;
        const tokens = part ? part.split(/\s+/) : [];
        return tokens.includes('virtual-keyboard-toggle') || tokens.includes('menu-toggle');
      });
      if (onToggle) return;
      window.mathVirtualKeyboard?.show();
    };
    const handleBlur = () => {
      // Defer briefly so focus that bounces back (a key tap / toggle) doesn't trigger a hide.
      // If focus truly left the field, hide the relevant keyboard.
      window.setTimeout(() => {
        const active = document.activeElement as Node | null;
        if (active === element || (active && element.contains(active))) return;
        if (studentKeyboard) mathKeyboard.hideKeyboard(element);
        else window.mathVirtualKeyboard?.hide();
      }, 100);
    };

    element.addEventListener('input', handleInput);
    element.addEventListener('focus', handleFocus);
    if (inTemplate) {
      element.addEventListener('beforeinput', handleBeforeInput);
      element.addEventListener('pointerup', handlePointerUp);
      element.addEventListener('keydown', handleKeyDown, true);
    }
    if (studentKeyboard) {
      element.addEventListener('blur', handleBlur);
    } else if (virtualKeyboard) {
      // Author: summon/dismiss MathLive's on-screen keyboard.
      element.addEventListener('pointerdown', handlePointerDown);
      element.addEventListener('blur', handleBlur);
    }

    return () => {
      element.removeEventListener('input', handleInput);
      element.removeEventListener('beforeinput', handleBeforeInput);
      element.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('keydown', handleKeyDown, true);
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('blur', handleBlur);
    };
  }, [
    value,
    onChange,
    normalizeLatex,
    slotExpression,
    slotValues,
    onSlotsChange,
    editsSlotExpression,
    virtualKeyboard,
    studentKeyboard,
    mathKeyboard
  ]);

  // Draw each slot's grading result. Runs after the effect above, which may have re-seeded the expression
  // (and so cleared every slot's state).
  useEffect(() => {
    const element = ref.current;
    if (element && slotExpression !== undefined) applySlotStates(element, slotStates);
  }, [slotExpression, slotStates, slotValues]);

  // While an author field is focused, keep it clear of MathLive's virtual keyboard — the keyboard is
  // a viewport-fixed bar that paints above every overlay, so the field (or its popover) must move
  // out from under it (bug 535585). The student keyboard's provider owns the equivalent lift for
  // its own dock.
  useEffect(() => {
    const element = ref.current;
    if (!element || !virtualKeyboard || studentKeyboard) return undefined;

    let stopLift: (() => void) | null = null;
    const start = () => {
      stopLift?.();
      stopLift = startVirtualKeyboardLift(element);
    };
    const stop = () => {
      stopLift?.();
      stopLift = null;
    };
    const handleBlur = () => {
      // Same bounce-guard as the keyboard hide: only stop once focus has truly left the field.
      window.setTimeout(() => {
        const active = document.activeElement as Node | null;
        if (active === element || (active && element.contains(active))) return;
        stop();
      }, 100);
    };

    element.addEventListener('focus', start);
    element.addEventListener('blur', handleBlur);
    // autoFocus may have landed before this effect ran.
    if (document.activeElement === element || element.contains(document.activeElement)) start();

    return () => {
      element.removeEventListener('focus', start);
      element.removeEventListener('blur', handleBlur);
      stop();
    };
  }, [virtualKeyboard, studentKeyboard]);

  // Reflect the disabled state once the element has upgraded.
  useEffect(() => {
    const element = ref.current;
    if (element && 'disabled' in element) element.disabled = Boolean(disabled);
  }, [disabled]);

  return (
    // @ts-expect-error `math-field` is a custom element registered by mathlive at runtime.
    <math-field
      ref={ref}
      // Student fields look like a plain input and hide mathlive's in-field toggles (see CSS).
      // A field inside a slot expression is printed math with slots in it (see MathLiveField.css).
      className={
        cx(className, studentKeyboard && 'mlf-student', slotExpression !== undefined && 'mlf-slots') || undefined
      }
      style={{ width: '100%', ...style }}
    />
  );
}
